import { useFloatingBoundary } from "@/components/context/FloatingBoundaryContext";
import { type RefObject, useEffect, useLayoutEffect, useState } from "react";

const DEFAULT_GAP = 8;
const DEFAULT_WIDTH = 220;
const DEFAULT_HEIGHT = 180;

type Spaces = { left: number; right: number; up: number; down: number };

export type FitResult = {
  fitsRight: boolean;
  fitsLeft: boolean;
  fitsDown: boolean;
  fitsUp: boolean;
  spaces: Spaces | null;
  hNeeded: number;
  vNeeded: number;
};

/**
 * Determine whether a floating panel fits on any side of an anchor, within a boundary.
 *
 * The hook measures available space inside a boundary element (from context or `boundaryRef`) and the
 * panel's natural size, then tells you if it fits on each side (right, left, down, up). It also exposes
 * the measured space on each side and the panel size needed along both axes. Measurements recompute when
 * the panel is open on resize/scroll and via `ResizeObserver`.
 *
 * @param anchorRef Ref to the anchor element that the panel is attached to.
 * @param panelRef  Ref to the floating panel element being positioned.
 * @param options   Configuration object:
 *   - boundaryRef?: Ref to a custom boundary element (defaults to viewport or provider context).
 *   - gap?:        Pixels to leave between anchor and panel (default: 8).
 *   - open:        Whether the panel is currently open (measuring happens only when open).
 *
 * @returns Object with:
 *   - fitsRight:   `boolean` — does the panel fit on the right?
 *   - fitsLeft:    `boolean` — does the panel fit on the left?
 *   - fitsDown:    `boolean` — does the panel fit below?
 *   - fitsUp:      `boolean` — does the panel fit above?
 *   - recalc:      `() => void` — trigger a manual recalculation.
 *   - spaces:      `{ left, right, up, down }` — available space each side (px), or null if not measured.
 *   - hNeeded:     `number` — panel width + gap.
 *   - vNeeded:     `number` — panel height + gap.
 */
export function usePreferredSideFit(
  anchorRef: RefObject<HTMLElement> | undefined,
  panelRef: RefObject<HTMLElement>,
  {
    boundaryRef,
    gap = DEFAULT_GAP,
    open,
  }: {
    boundaryRef?: RefObject<HTMLElement>;
    gap?: number;
    open: boolean;
  },
): FitResult & { recalc: () => void } {
  const [result, setResult] = useState<FitResult>({
    fitsRight: true,
    fitsLeft: true,
    fitsDown: true,
    fitsUp: true,
    spaces: null,
    hNeeded: DEFAULT_WIDTH + gap,
    vNeeded: DEFAULT_HEIGHT + gap,
  });

  const ctxBoundary = useFloatingBoundary();

  const recalc = () => {
    const anchorEl = anchorRef?.current;
    if (!anchorEl) {
      return;
    }
    const panelEl = panelRef.current;

    const boundaryElement = boundaryRef?.current ?? ctxBoundary?.current ?? null;
    const boundaryRect = boundaryElement
      ? boundaryElement.getBoundingClientRect()
      : ({ left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight } as DOMRect);

    const rect = anchorEl.getBoundingClientRect();

    const panelWidth = (panelEl?.scrollWidth ?? panelEl?.offsetWidth ?? DEFAULT_WIDTH) + gap;
    const panelHeight = (panelEl?.scrollHeight ?? panelEl?.offsetHeight ?? DEFAULT_HEIGHT) + gap;

    const spaceRight = boundaryRect.right - rect.right;
    const spaceLeft = rect.left - boundaryRect.left;
    const spaceDown = boundaryRect.bottom - rect.bottom;
    const spaceUp = rect.top - boundaryRect.top;

    const spaces: Spaces = {
      left: spaceLeft,
      right: spaceRight,
      up: spaceUp,
      down: spaceDown,
    };

    setResult({
      fitsRight: spaceRight >= panelWidth,
      fitsLeft: spaceLeft >= panelWidth,
      fitsDown: spaceDown >= panelHeight,
      fitsUp: spaceUp >= panelHeight,
      spaces,
      hNeeded: panelWidth,
      vNeeded: panelHeight,
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    // Ensure the first measurement runs before first paint
    recalc();
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const boundaryElement = boundaryRef?.current ?? ctxBoundary?.current ?? null;

    recalc();
    const onResize = () => recalc();
    const onScroll = () => recalc();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });

    const scrollBoundary = boundaryElement;
    if (scrollBoundary) {
      scrollBoundary.addEventListener("scroll", onScroll, { passive: true });
    }

    let resizeObserver: ResizeObserver | undefined;
    // If RO is missing (e.g., in tests), the hook skips the observer and still works via resize/scroll listeners and manual recalc.
    const RO: typeof ResizeObserver | undefined =
      typeof window !== "undefined" ? (window as any).ResizeObserver : undefined;
    if (panelRef.current && typeof RO !== "undefined") {
      resizeObserver = new RO(() => recalc());
      resizeObserver.observe(panelRef.current);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
      if (scrollBoundary) {
        scrollBoundary.removeEventListener("scroll", onScroll as EventListener);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [open]);

  return {
    ...result,
    recalc,
  };
}
